import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Card, CardContent, Typography, Box, Grid, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip, TextField,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { Badge, Warning, Refresh, CheckCircle } from '@mui/icons-material';
import { useAuth } from '../App';
import axios from 'axios';

const API_BASE = API_BASE_URL;
const USC_GREEN = '#046A38';

const AdminAlumniIdCleanupPage = () => {
  const { user } = useAuth();

  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [results, setResults] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);

  // How many broken IDs to fix in this run: 'all' or a specific small chunk
  const [fixMode, setFixMode] = useState('10');

  const authHeader = { headers: { Authorization: `Bearer ${user?.idToken}` } };

  useEffect(() => {
    if (user?.idToken) loadAudit();
  }, [user?.idToken]);

  const loadAudit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/admin/alumni-ids/audit`, authHeader);
      setAudit(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load Alumni ID audit');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmFix = async () => {
    setFixing(true);
    setError('');
    setDiagnostics(null);
    try {
      const payload = fixMode === 'all' ? {} : { limit: parseInt(fixMode, 10) };
      const res = await axios.post(`${API_BASE}/admin/alumni-ids/fix`, payload, authHeader);
      setResults(res.data.results || []);
      if (res.data.sheetsResponse) setDiagnostics({ sheetsResponse: res.data.sheetsResponse });
      setConfirmOpen(false);
      await loadAudit();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fix Alumni IDs');
      const data = err.response?.data;
      if (data?.sheetsResponse || data?.mismatches) {
        setDiagnostics({ sheetsResponse: data.sheetsResponse, mismatches: data.mismatches });
      }
      setConfirmOpen(false);
    } finally {
      setFixing(false);
    }
  };

  const StatCard = ({ label, value, color }) => (
    <Card elevation={2}>
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: color || USC_GREEN }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: USC_GREEN, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge fontSize="large" /> Alumni ID Cleanup
        </Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadAudit} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Finds registered alumni whose Alumni ID is not a clean 12-digit number — this catches
        Google Sheets corruption (e.g. IDs displayed as <code>2.03E+11</code>) and older IDs
        generated with an inconsistent length. Nothing is changed until you review the list below
        and confirm.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {diagnostics && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDiagnostics(null)}>
          <Typography variant="body2" component="div">
            {diagnostics.sheetsResponse && (
              <div>
                Google Sheets confirmed: {diagnostics.sheetsResponse.totalUpdatedCells ?? '?'} cell(s),{' '}
                {diagnostics.sheetsResponse.totalUpdatedRows ?? '?'} row(s),{' '}
                {diagnostics.sheetsResponse.totalUpdatedRanges ?? '?'} range(s) updated.
              </div>
            )}
            {diagnostics.mismatches && diagnostics.mismatches.length > 0 && (
              <div style={{ marginTop: 4 }}>
                First mismatches (row: expected → actual):{' '}
                {diagnostics.mismatches.map((m, i) => (
                  <code key={i} style={{ marginRight: 8 }}>
                    {m.row}: {m.expected}→{m.actual ?? '(blank)'}
                  </code>
                ))}
              </div>
            )}
          </Typography>
        </Alert>
      )}

      {results && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }} onClose={() => setResults(null)}>
          Reassigned {results.length} Alumni ID{results.length !== 1 ? 's' : ''} successfully.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : audit ? (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}><StatCard label="Registered alumni" value={audit.totalRegistered} /></Grid>
            <Grid item xs={4}><StatCard label="Valid IDs" value={audit.validCount} color="#2e7d32" /></Grid>
            <Grid item xs={4}><StatCard label="Broken IDs" value={audit.brokenCount} color="#c62828" /></Grid>
          </Grid>

          {audit.brokenCount === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 5 }}>
                <CheckCircle sx={{ fontSize: 56, color: '#2e7d32', mb: 1 }} />
                <Typography variant="h6">All Alumni IDs are clean</Typography>
                <Typography variant="body2" color="text.secondary">
                  Every registered alumnus has a valid 12-digit Alumni ID.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Preview — {audit.brokenCount} ID{audit.brokenCount !== 1 ? 's' : ''} will be reassigned
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                      size="small" exclusive value={fixMode}
                      onChange={(e, val) => val && setFixMode(val)}
                    >
                      <ToggleButton value="10">First 10</ToggleButton>
                      <ToggleButton value="25">First 25</ToggleButton>
                      <ToggleButton value="all">All ({audit.brokenCount})</ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                      variant="contained" color="error" startIcon={<Warning />}
                      onClick={() => setConfirmOpen(true)}
                    >
                      Fix {fixMode === 'all' ? `All ${audit.brokenCount}` : `First ${fixMode}`}
                    </Button>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Fixing in smaller batches (e.g. 10 at a time) is safer to verify and less likely to
                  hit Google Sheets' write-rate limit. Run it again to process the next batch.
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Name</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Current (broken) ID</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {audit.broken.map((r) => (
                        <TableRow key={r.rowIndex} hover>
                          <TableCell>{r.name || '—'}</TableCell>
                          <TableCell>{r.email || '—'}</TableCell>
                          <TableCell><Chip label={r.currentId || '(blank-looking)'} color="warning" size="small" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      ) : null}

      {results && results.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Last fix — results</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Old ID</strong></TableCell>
                    <TableCell><strong>New ID</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{r.name || '—'}</TableCell>
                      <TableCell>{r.email || '—'}</TableCell>
                      <TableCell><Chip label={r.oldId || '(blank-looking)'} size="small" variant="outlined" /></TableCell>
                      <TableCell><Chip label={r.newId} color="success" size="small" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => !fixing && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" /> Confirm Bulk Alumni ID Fix
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            This will assign a brand-new 12-digit Alumni ID to{' '}
            <strong>
              {fixMode === 'all' ? (audit?.brokenCount || 0) : Math.min(parseInt(fixMode, 10), audit?.brokenCount || 0)}
            </strong>{' '}
            record(s){fixMode !== 'all' ? ' (the first batch — run again afterward for the rest)' : ''}.
            This cannot be undone — anyone with a printed/saved ID card using one of the old broken
            IDs will need a new card generated afterward.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            IDs that are already a valid 12-digit number are not touched.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={fixing}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={handleConfirmFix} disabled={fixing}
            startIcon={fixing ? <CircularProgress size={18} color="inherit" /> : <Warning />}
          >
            {fixing ? 'Fixing...' : 'Yes, Reassign IDs'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminAlumniIdCleanupPage;
